import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Button,
  Alert,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import RatePlanChannelForm from "./RatePlanChannelForm";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RatePlanChannel = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // Define table columns
  const columns = [
    { title: "ID", field: "id" },
    { title: "Rate Plan ID", field: "rate_plan_id" },
    { title: "Channel ID", field: "channel_id" },
    { title: "Room Type", field: "room_type" },
    { title: "Channel Rate Plan ID", field: "channel_rate_plan_id" },
    { title: "Activity Type", field: "activity_type" },
    { title: "Is Active", field: "is_active", type: "boolean" },
  ];

  // Add handleEdit function
  const handleEdit = (row) => {
    console.log("Editing row:", row); // Add logging to debug
    setEditData({
      id: row.id,
      rate_plan_id: row.rate_plan_id,
      channel_id: row.channel_id,
      room_type: row.room_type,
      channel_rate_plan_id: row.channel_rate_plan_id,
      activity_type: row.activity_type,
      is_active: row.is_active,
    });
    setShowForm(true);
  };

  // Add this function to fetch data
  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/RatePlanChannel");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Add handleClose for Snackbar
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Update handleFormSubmit
  const handleFormSubmit = async (formData) => {
    try {
      if (formData.data.id) {
        // Update existing rate plan channel
        const response = await fetch(
          `https://api.esthell.com/RatePlanChannel/${formData.data.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update rate plan");
        }
        setNotification({
          open: true,
          message: "Rate Plan Channel updated successfully!",
          type: "success",
        });
      } else {
        // Generate channel_rate_plan_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "RPC",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate channel rate plan ID");
        }

        const numberingData = await numberingResponse.json();
        const nextRpcId = (
          parseInt(numberingData[0].last_gen_number) + 1
        ).toString(); // Convert to string

        // Update formData with the generated channel_rate_plan_id
        formData.data.channel_rate_plan_id = nextRpcId;

        // Create new rate plan channel
        const response = await fetch(
          "https://api.esthell.com/create/RatePlanChannel",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create rate plan");
        }
        setNotification({
          open: true,
          message: "Rate Plan Channel created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  // Update handleDelete
  const handleDelete = async (row) => {
    try {
      const response = await fetch(
        `https://api.esthell.com/RatePlanChannel/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete rate plan");
      }

      const result = await response.json();
      if (result.success) {
        fetchData();
        setNotification({
          open: true,
          message: "Rate Plan Channel deleted successfully!",
          type: "success",
        });
      } else {
        throw new Error(result.message || "Failed to delete rate plan");
      }
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };
  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RatePlanChannels");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      file,
      `RatePlanChannels_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };
  // Add Snackbar to the return statement, just before the closing Box tag
  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Rate Plan Channels
        </Typography>
        <Button
          variant="contained"
          onClick={handleExportToExcel}
          sx={{ ml: 2 }}
        >
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      {/* Form Dialog */}
      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <RatePlanChannelForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            onSubmit={handleFormSubmit}
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RatePlanChannel;
